import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, Target, Trophy, Percent, Calculator } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtMoney } from "@/lib/format";
import RiyalSymbol from "@/components/RiyalSymbol";

const ALLOWED_ROLES = new Set(["general_manager", "deputy_manager", "accountant"]);

export default function BonusPolicyPage() {
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();
  const navigate = useNavigate();
  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code;
  const canEdit = isRoot || ALLOWED_ROLES.has(roleCode);

  const [policy, setPolicy] = useState(null);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const T = lang === "ar" ? AR : EN;

  const load = async () => {
    setErr("");
    try {
      const [p, o] = await Promise.all([
        api.get("/bonus-policy"),
        api.get("/admin/earnings-overview").catch(() => ({ data: null })),
      ]);
      setPolicy(p.data);
      setOverview(o.data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { if (canEdit) load(); }, [canEdit]);

  const css = makeStyles(theme);
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  if (!canEdit) {
    return (
      <div dir={dir} style={{ padding: 32, color: theme.errorText, textAlign: "center" }}>
        {T.no_access}
      </div>
    );
  }
  if (!policy) {
    return <div dir={dir} style={{ ...css.page, color: theme.textMuted }}>…</div>;
  }

  const u = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        target_amount: Number(policy.target_amount),
        bonus_percent: Number(policy.bonus_percent),
        golden_ceiling: Number(policy.golden_ceiling),
        golden_bonus_extra: Number(policy.golden_bonus_extra),
        calculation_mode: policy.calculation_mode,
      };
      await api.put("/admin/bonus-policy", payload);
      setMsg(T.saved);
      setTimeout(() => setMsg(""), 3000);
      load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div dir={dir} style={css.page} data-testid="bonus-policy-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="bonus-back">
          <BackIcon size={14} strokeWidth={2.4} /> {T.back}
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>{T.title}</h1>
          <p style={css.sub}>{T.sub}</p>
        </div>
      </header>

      {err && <div style={css.err}>{err}</div>}
      {msg && <div style={css.ok}>{msg}</div>}

      <section style={css.card}>
        <h2 style={css.cardTitle}><Calculator size={16} /> {T.section_policy}</h2>
        <div style={css.modeRow}>
          <ModeBtn
            theme={theme}
            active={policy.calculation_mode === "income"}
            onClick={() => u("calculation_mode", "income")}
            title={T.mode_income}
            desc={T.mode_income_desc}
            testid="mode-income"
          />
          <ModeBtn
            theme={theme}
            active={policy.calculation_mode === "net"}
            onClick={() => u("calculation_mode", "net")}
            title={T.mode_net}
            desc={T.mode_net_desc}
            testid="mode-net"
          />
        </div>
        <div style={css.grid}>
          <NumField label={T.f_target} icon={Target} value={policy.target_amount} onChange={(v) => u("target_amount", v)} suffix={T.sar} theme={theme} testid="f-target" />
          <NumField label={T.f_percent} icon={Percent} value={policy.bonus_percent} onChange={(v) => u("bonus_percent", v)} suffix="%" theme={theme} testid="f-percent" />
          <NumField label={T.f_ceiling} icon={Trophy} value={policy.golden_ceiling} onChange={(v) => u("golden_ceiling", v)} suffix={T.sar} theme={theme} testid="f-ceiling" />
          <NumField label={T.f_extra} icon={Trophy} value={policy.golden_bonus_extra} onChange={(v) => u("golden_bonus_extra", v)} suffix={T.sar} theme={theme} testid="f-extra" />
        </div>
        <div style={css.actions}>
          <button onClick={save} disabled={busy} style={css.btnGold} data-testid="bonus-save">
            <Save size={14} strokeWidth={2.4} /> {busy ? T.saving : T.save}
          </button>
        </div>

        <PreviewExamples policy={policy} T={T} theme={theme} />
      </section>

      {overview && overview.rows && (
        <section style={css.card}>
          <h2 style={css.cardTitle}><Trophy size={16} /> {T.section_overview} — {overview.period}</h2>
          {overview.rows.length === 0 ? (
            <div style={css.emptyMini}>{T.empty_overview}</div>
          ) : (
            <div style={css.table}>
              <div style={css.tableHead}>
                <span>{T.col_employee}</span>
                <span>{T.col_apps}</span>
                <span>{T.col_income}</span>
                <span>{T.col_net}</span>
                <span>{T.col_bonus}</span>
              </div>
              {overview.rows.map((r) => (
                <div key={r.user_id} style={css.tableRow} data-testid={`overview-${r.username}`}>
                  <span style={{ fontWeight: 800 }}>{r.full_name}</span>
                  <span style={css.cellMono}>{r.applications_count}</span>
                  <span style={css.cellMoney}>{fmtMoney(r.income_total)}</span>
                  <span style={css.cellMoney}>{fmtMoney(r.office_revenue_total)}</span>
                  <span style={{ ...css.cellMoney, color: r.bonus.total_bonus > 0 ? "#16a34a" : theme.textMuted }}>
                    {fmtMoney(r.bonus.total_bonus)}
                    {r.bonus.golden_unlocked && <Trophy size={11} style={{ marginInlineStart: 4, color: theme.gold }} />}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function ModeBtn({ theme, active, onClick, title, desc, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        textAlign: "inherit", color: "inherit", font: "inherit",
        flex: "1 1 240px", padding: "14px 16px",
        background: active ? "rgba(212,175,55,0.10)" : "transparent",
        border: `2px solid ${active ? theme.gold : theme.border}`,
        borderRadius: 12, cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: 900, color: active ? theme.gold : theme.text, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, lineHeight: 1.6 }}>{desc}</div>
    </button>
  );
}

function NumField({ label, icon: Icon, value, onChange, suffix, theme, testid }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 6, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Icon size={12} strokeWidth={2.4} /> {label}
      </div>
      <div style={{
        display: "flex", alignItems: "center",
        background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
        borderRadius: 10, paddingInlineEnd: 12,
      }}>
        <input
          type="number" step="0.01" min="0"
          value={value} onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testid}
          style={{
            flex: 1, padding: "11px 14px",
            border: "none", background: "transparent",
            color: theme.text, fontSize: 14, fontFamily: "monospace",
            outline: "none", direction: "ltr",
          }}
        />
        <span style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted }}>{suffix}</span>
      </div>
    </label>
  );
}

function PreviewExamples({ policy, T, theme }) {
  const samples = [
    Number(policy.target_amount),
    Number(policy.target_amount) + 1000,
    Number(policy.golden_ceiling),
    Number(policy.golden_ceiling) + 5000,
  ];
  const compute = (earnings) => {
    const target = Number(policy.target_amount) || 0;
    const percent = Number(policy.bonus_percent) || 0;
    const ceiling = Number(policy.golden_ceiling) || 0;
    const extra = Number(policy.golden_bonus_extra) || 0;
    let base = 0; if (earnings > target) base = (earnings - target) * percent / 100;
    const golden = ceiling > 0 && earnings >= ceiling ? extra : 0;
    return Math.round((base + golden) * 100) / 100;
  };
  return (
    <div style={{
      marginTop: 18, padding: 14,
      background: theme.surfaceAlt, borderRadius: 10,
      border: `1px dashed ${theme.borderSoft}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: theme.textMuted, marginBottom: 8 }}>{T.preview_title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {samples.map((s) => {
          const b = compute(s);
          return (
            <div key={s} style={{
              padding: 10, background: theme.surface,
              border: `1px solid ${theme.borderSoft}`, borderRadius: 8,
            }}>
              <div style={{ fontSize: 11, color: theme.textMuted }}>{T.if_earns}</div>
              <div style={{ fontWeight: 900, fontFamily: "monospace", direction: "ltr", color: theme.text }}>
                {fmtMoney(s)} <RiyalSymbol size={12} />
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: theme.textMuted }}>{T.bonus_eq}</div>
              <div style={{ fontWeight: 900, color: b > 0 ? "#16a34a" : theme.textMuted, fontFamily: "monospace", direction: "ltr" }}>
                {fmtMoney(b)} <RiyalSymbol size={12} color={b > 0 ? "#16a34a" : theme.textMuted} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const AR = {
  title: "سياسة البونص الشهرية",
  sub: "تعديلها متاح لـ STAS، عبدالعزيز، نايف، والمحاسب — التغيير ينعكس فوراً",
  back: "رجوع",
  no_access: "غير مصرح",
  section_policy: "إعدادات البونص",
  mode_income: "الدخل الكامل",
  mode_income_desc: "البونص يُحسب على مجموع رسوم المعاملات (افتراضي معتمد من عبدالعزيز)",
  mode_net: "صافي المكتب",
  mode_net_desc: "البونص يُحسب فقط على البنود المُحتسبة كدخل مكتب (يستثني الـ passthrough)",
  f_target: "المبلغ المستهدف",
  f_percent: "نسبة البونص",
  f_ceiling: "السقف الذهبي",
  f_extra: "مكافأة بلوغ السقف",
  sar: "ر.س",
  save: "حفظ السياسة",
  saving: "جاري الحفظ…",
  saved: "تم الحفظ ✓",
  preview_title: "أمثلة مباشرة",
  if_earns: "إذا حقّق",
  bonus_eq: "البونص",
  section_overview: "أداء الموظفين هذا الشهر",
  empty_overview: "لا توجد بيانات دخل لهذا الشهر بعد",
  col_employee: "الموظف",
  col_apps: "المعاملات",
  col_income: "الدخل",
  col_net: "الصافي",
  col_bonus: "البونص",
};

const EN = {
  title: "Monthly Bonus Policy",
  sub: "Editable by STAS, Abdulaziz, Naif, and the Accountant — changes propagate instantly",
  back: "Back",
  no_access: "Not authorized",
  section_policy: "Bonus settings",
  mode_income: "Full income",
  mode_income_desc: "Bonus computed on total application fees (default approved by Abdulaziz)",
  mode_net: "Office net",
  mode_net_desc: "Bonus computed only on items marked as office revenue (excludes passthrough)",
  f_target: "Target amount",
  f_percent: "Bonus percent",
  f_ceiling: "Golden ceiling",
  f_extra: "Golden bonus",
  sar: "SAR",
  save: "Save policy",
  saving: "Saving…",
  saved: "Saved ✓",
  preview_title: "Live examples",
  if_earns: "If they earn",
  bonus_eq: "Bonus",
  section_overview: "Employee performance this month",
  empty_overview: "No income data yet for this month",
  col_employee: "Employee",
  col_apps: "Apps",
  col_income: "Income",
  col_net: "Net",
  col_bonus: "Bonus",
};

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh", background: theme.bg, color: theme.text,
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    padding: "20px 16px", maxWidth: 1100, margin: "0 auto",
  },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700,
    fontFamily: "inherit",
  },
  title: { color: theme.gold, fontSize: 22, fontWeight: 900, margin: 0 },
  sub: { fontSize: 12, color: theme.textMuted, margin: "4px 0 0", lineHeight: 1.6 },
  card: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14, padding: 22, marginBottom: 16,
  },
  cardTitle: {
    margin: "0 0 16px", fontSize: 15, fontWeight: 900, color: theme.gold,
    display: "inline-flex", alignItems: "center", gap: 8,
  },
  modeRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 },
  actions: { display: "flex", justifyContent: "flex-end", marginTop: 18, gap: 10 },
  btnGold: {
    padding: "11px 20px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 10, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
    fontSize: 13, fontWeight: 900, fontFamily: "inherit",
  },
  err: { padding: 12, background: theme.errorBg, color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, marginBottom: 12, fontSize: 13 },
  ok: { padding: 12, background: "rgba(22,163,74,0.10)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.30)", borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 700 },
  emptyMini: { padding: 24, textAlign: "center", color: theme.textMuted, fontSize: 13 },
  table: { border: `1px solid ${theme.borderSoft}`, borderRadius: 10, overflow: "hidden" },
  tableHead: {
    display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr 1.2fr",
    gap: 10, padding: "12px 14px",
    background: theme.surfaceAlt,
    borderBottom: `1px solid ${theme.borderSoft}`,
    fontSize: 11, fontWeight: 800, color: theme.textMuted, letterSpacing: 0.3,
  },
  tableRow: {
    display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr 1.2fr",
    gap: 10, padding: "12px 14px",
    borderBottom: `1px solid ${theme.borderSoft}`,
    alignItems: "center", fontSize: 13,
  },
  cellMono: { fontFamily: "monospace", direction: "ltr" },
  cellMoney: { fontFamily: "monospace", fontWeight: 800, color: theme.gold, direction: "ltr" },
});
